import {
  Navigate,
  useLocation,
  useNavigation,
  useSubmit,
} from "react-router-dom";
import { VerifyCodeFormData } from "../verifyCode/types/verifyCodeTypes";
import { Building, Code, Monitor, Terminal, Users } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  resetPasswordFormData,
  resetPasswordSchema,
} from "./types/resetPasswordTypes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
  const { t } = useTranslation();
  const submit = useSubmit();
  const navigation = useNavigation();
  const location = useLocation();
  const data = location.state satisfies VerifyCodeFormData;
  const isSubmitting = navigation.state === "submitting";

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<resetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      newPasswordConfirm: "",
      code: data?.code || "",
      email: data?.email || "",
    },
  });

  if (!data) return <Navigate to="/forgot-password" replace />;

  const onSubmit = (data: resetPasswordFormData) => {
    submit(data, {
      method: "post",
      action: "/reset-password",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-6xl bg-white rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden">
        {/* Grid for separating form and Coding Illustration Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[400px] lg:min-h-[600px]">
          {/* Form Section */}
          <div className="flex flex-col justify-center p-4 sm:p-6 lg:p-12">
            {/* Form Input Width Adjust */}
            <div className="w-full max-w-md mx-auto">
              {/* Form Header */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary rounded-lg p-2">
                    <Code className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold">{t('resetPassword.title')}</h1>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">
                  {t('resetPassword.subtitle')}
                </p>
              </div>

              {/* Form Fields */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <input type="hidden" name="email" value={data.email} />
                <input type="hidden" name="code" value={data.code} />

                <div className="space-y-2">
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('resetPassword.newPassword')}
                  </label>
                  <input
                    id="newPassword"
                    {...register("newPassword")}
                    type="password"
                    required
                    placeholder="••••••••"
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.newPassword && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.newPassword.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="newPasswordConfirm"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('resetPassword.newPasswordConfirm')}
                  </label>
                  <input
                    id="newPasswordConfirm"
                    {...register("newPasswordConfirm")}
                    type="password"
                    required
                    placeholder="••••••••"
                    className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {errors.newPasswordConfirm && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.newPasswordConfirm.message}
                    </p>
                  )}
                </div>

                <button
                  disabled={isSubmitting || !isValid}
                  type="submit"
                  className={`w-full h-11 text-base font-medium text-white rounded-md transition-colors duration-300 ${
                    isSubmitting || !isValid
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-primary-600 hover:shadow-lg cursor-pointer"
                  }`}
                >
                  {isSubmitting ? t('resetPassword.updating') : t('resetPassword.updatePassword')}
                </button>
              </form>
            </div>
          </div>

          {/* Coding Illustration Section - Hidden on mobile */}
          <div className="hidden lg:flex bg-gradient-to-br from-primary-50 to-primary-100 items-center justify-center p-8 lg:p-12">
            <div className="relative w-full max-w-lg">
              {/* Main Illustration Container */}
              <div className="relative bg-white rounded-2xl shadow-xl p-6 border">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Terminal className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-gray-700">
                      Kod Editörü
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                </div>

                {/* Code Editor Mockup */}
                <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">1</span>
                    <span className="text-purple-400">function</span>
                    <span className="text-blue-400">resetPassword</span>
                    <span className="text-yellow-400">(newPassword)</span>
                    <span className="text-white">{"{"}</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-gray-500">2</span>
                    <span className="text-red-400">const</span>
                    <span className="text-blue-300">hashedPassword</span>
                    <span className="text-white">=</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-8">
                    <span className="text-gray-500">3</span>
                    <span className="text-purple-400">await</span>
                    <span className="text-blue-400">bcrypt</span>
                    <span className="text-white">.hash(newPassword, 10)</span>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-gray-500">4</span>
                    <span className="text-purple-400">return</span>
                    <span className="text-green-400">'Şifre güncellendi!'</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">5</span>
                    <span className="text-white">{"}"}</span>
                  </div>
                </div>

                {/* Güvenlik Özellikleri */}
                <div className="grid grid-cols-3 gap-3 mt-6">
                  <div className="text-center p-3 bg-primary-50 rounded-lg">
                    <Monitor className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                    <div className="text-lg font-bold text-primary-600">
                      SSL
                    </div>
                    <div className="text-xs text-primary-600">Güvenlik</div>
                  </div>
                  <div className="text-center p-3 bg-primary-100 rounded-lg">
                    <Users className="h-5 w-5 text-primary-700 mx-auto mb-1" />
                    <div className="text-lg font-bold text-primary-700">
                      2FA
                    </div>
                    <div className="text-xs text-primary-700">Doğrulama</div>
                  </div>
                  <div className="text-center p-3 bg-primary-200 rounded-lg">
                    <Building className="h-5 w-5 text-primary-800 mx-auto mb-1" />
                    <div className="text-lg font-bold text-primary-800">
                      Hash
                    </div>
                    <div className="text-xs text-primary-800">Şifreleme</div>
                  </div>
                </div>

                {/* Programming Languages */}
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Güvenlik Teknolojileri
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {["bcrypt", "JWT", "OAuth", "HTTPS", "Encryption"].map(
                      (tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1 bg-gradient-to-r from-primary to-primary-600 text-white text-xs rounded-full font-medium"
                        >
                          {tech}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-3 -right-3 bg-primary-400 rounded-full p-2 shadow-lg animate-bounce">
                <Code className="h-5 w-5 text-primary-900" />
              </div>
              <div className="absolute -bottom-3 -left-3 bg-primary rounded-full p-2 shadow-lg animate-pulse">
                <Terminal className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ResetPassword;
